import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserResponse } from '../Model/User';

interface SidebarProps {
  user?: UserResponse | null;
  logout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ user, logout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  const getInitials = (u?: UserResponse | null) =>
    u?.username ? u.username.charAt(0).toUpperCase() : 'U';

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navGroups = [
    {
      label: 'Projects',
      items: [
        {
          path: '/sites',
          label: 'All Projects',
          badge: null,
          icon: (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
              <rect x="1" y="1" width="6" height="6" rx="1.5"/>
              <rect x="9" y="1" width="6" height="6" rx="1.5"/>
              <rect x="1" y="9" width="6" height="6" rx="1.5"/>
              <rect x="9" y="9" width="6" height="6" rx="1.5"/>
            </svg>
          ),
        },
        {
          path: '/deploy',
          label: 'New Project',
          badge: null,
          icon: (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
              <path d="M8 2v12M2 8h12"/>
            </svg>
          ),
        },
        {
          path: '/previews',
          label: 'Preview Environments',
          badge: 'New',
          icon: (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
              <path d="M2 4h12v9a1 1 0 01-1 1H3a1 1 0 01-1-1V4zM5 4V2h6v2"/>
            </svg>
          ),
        },
      ],
    },
    {
      label: 'Infrastructure',
      items: [
        {
          path: '/infrastructure',
          label: 'Infrastructure',
          badge: 'New',
          icon: (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
              <rect x="1" y="1" width="14" height="5" rx="1.5"/>
              <rect x="1" y="10" width="14" height="5" rx="1.5"/>
              <circle cx="3.5" cy="3.5" r=".8" fill="currentColor"/>
              <circle cx="3.5" cy="12.5" r=".8" fill="currentColor"/>
            </svg>
          ),
        },
        {
          path: '/cloud',
          label: 'Cloud Accounts',
          badge: null,
          icon: (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
              <path d="M12 10a4 4 0 00-8 0M5 6a3 3 0 116 0"/>
            </svg>
          ),
        },
        // {
        //   path: '/cost',
        //   label: 'Cost Monitor',
        //   badge: 'New',
        //   icon: (
        //     <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
        //       <path d="M8 2v2M8 12v2M4.93 4.93l1.41 1.41M9.66 9.66l1.41 1.41M2 8h2M12 8h2"/>
        //       <circle cx="8" cy="8" r="3"/>
        //     </svg>
        //   ),
        // },
        {
          path: '/monitoring',
          label: 'Monitor',
          badge: 'New',
          icon: (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
              <path d="M8 2v2M8 12v2M4.93 4.93l1.41 1.41M9.66 9.66l1.41 1.41M2 8h2M12 8h2"/>
              <circle cx="8" cy="8" r="3"/>
            </svg>
          ),
        },
      ],
    },
    {
      label: 'Account',
      items: [
        // {
        //   path: '/team',
        //   label: 'Team & RBAC',
        //   badge: 'New',
        //   icon: (
        //     <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
        //       <circle cx="6" cy="5" r="2.5"/>
        //       <path d="M1 13c0-2.761 2.239-5 5-5"/>
        //       <circle cx="11.5" cy="5" r="2.5"/>
        //       <path d="M9.5 13c.5-1.5 2-3.5 4-4"/>
        //     </svg>
        //   ),
        // },
        {
          path: '/profile',
          label: 'Profile',
          badge: null,
          icon: (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
              <circle cx="8" cy="5" r="3"/>
              <path d="M2 14c0-3.314 2.686-6 6-6s6 2.686 6 6"/>
            </svg>
          ),
        },
        {
          path: '/plans',
          label: 'Plans & Billing',
          badge: null,
          icon: (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
              <rect x="1" y="3" width="14" height="10" rx="2"/>
              <path d="M1 6h14"/>
            </svg>
          ),
        },
      ],
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  const NavItem = ({ item, mobile = false }: { item: typeof navGroups[0]['items'][0]; mobile?: boolean }) => (
    <Link
      to={item.path}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: mobile ? '9px' : '8px',
        padding: '6px 9px',
        borderRadius: '5px',
        color: isActive(item.path) ? '#818cf8' : '#8e8ea8',
        background: isActive(item.path) ? 'rgba(99,102,241,0.14)' : 'transparent',
        fontSize: '12.5px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all .15s',
        marginBottom: '1px',
        textDecoration: 'none',
        userSelect: 'none' as const,
      }}
      onMouseEnter={e => {
        if (!isActive(item.path)) {
          (e.currentTarget as HTMLElement).style.background = '#17171e';
          (e.currentTarget as HTMLElement).style.color = '#eeeef5';
        }
      }}
      onMouseLeave={e => {
        if (!isActive(item.path)) {
          (e.currentTarget as HTMLElement).style.background = 'transparent';
          (e.currentTarget as HTMLElement).style.color = '#8e8ea8';
        }
      }}
    >
      {item.icon}
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.badge && (
        <span style={{
          fontSize: '9px', fontWeight: 700, textTransform: 'uppercase' as const,
          letterSpacing: '.05em', padding: '1px 5px', borderRadius: '3px',
          color: '#22c55e', background: 'rgba(34,197,94,0.1)',
        }}>
          {item.badge}
        </span>
      )}
    </Link>
  );

  const sidebarContent = (
    <>
      {/* Logo */}
      <div 
      onClick={() => navigate('/sites')}
      style={{
        padding: '16px 14px 13px',
        borderBottom: '1px solid #26262f',
        display: 'flex', alignItems: 'center', gap: '9px',
      }}>
        <div style={{
          width: '28px', height: '28px',
          background: 'linear-gradient(135deg,#6366f1,#a855f7)',
          borderRadius: '7px', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontFamily: "'Syne',sans-serif",
          fontWeight: 800, fontSize: '12px', color: '#fff', flexShrink: 0,
        }}>Qx</div>
        <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: '14px', color: '#eeeef5' }}>
          Qix<span style={{ color: '#818cf8' }}>Deploy</span>
        </span>
      </div>

      {/* Nav */}
      <nav style={{ padding: '8px 7px', flex: 1, overflowY: 'auto' as const }}>
        {navGroups.map(group => (
          <div key={group.label} style={{ marginBottom: '4px' }}>
            <div style={{
              fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase' as const,
              letterSpacing: '.09em', color: '#52525f', padding: '7px 8px 3px',
            }}>
              {group.label}
            </div>
            {group.items.map(item => (
              <NavItem key={item.path} item={item} mobile={!isDesktop} />
            ))}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div style={{ padding: '9px 7px', borderTop: '1px solid #26262f' }}>
        <div style={{
            background: 'linear-gradient(135deg,rgba(99,102,241,.08),rgba(168,85,247,.08))',
            border: '1px solid rgba(99,102,241,.2)',
            borderRadius: '6px', padding: '6px 9px',
            fontSize: '11px', color: '#818cf8', marginBottom: '7px', cursor: 'pointer',
          }}
            onClick={() => navigate('/plans')}
          >
            ⚡ {user?.current_month_projects ?? 0}/{user?.projects_allowed_this_month ?? 0} projects · <strong>Upgrade</strong>
        </div>

        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '6px 9px', borderRadius: '5px', cursor: 'pointer',
            transition: 'background .15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#17171e')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <div style={{
            width: '24px', height: '24px', borderRadius: '50%',
            background: 'linear-gradient(135deg,#6366f1,#a855f7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '9px', fontWeight: 700, color: '#fff', flexShrink: 0,
          }}>
            {getInitials(user)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '12px', fontWeight: 500, color: '#eeeef5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
              {user?.username || 'User'}
            </div>
            <div style={{ fontSize: '10.5px', color: '#52525f' }}>{user?.plan_details.plan_name}</div>
          </div>
          <button
            onClick={logout}
            title="Logout"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#52525f', padding: '3px', borderRadius: '4px',
              display: 'flex', alignItems: 'center', transition: 'color .15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = '#f43f5e')}
            onMouseLeave={e => (e.currentTarget.style.color = '#52525f')}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M5 2H2.5a1 1 0 00-1 1v8a1 1 0 001 1H5M9 10l3-3-3-3M12 7H5"/>
            </svg>
          </button>
        </div>
      </div>
    </>
  );

  if (isDesktop) {
    return (
      <aside style={{
        width: '220px', height: '100vh', background: '#111116',
        borderRight: '1px solid #26262f', display: 'flex',
        flexDirection: 'column', position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 100,
      }}>
        {sidebarContent}
      </aside>
    );
  }

  return (
    <>
      {/* Mobile header */}
      <header style={{
        background: '#111116', borderBottom: '1px solid #26262f',
        padding: '12px 16px', position: 'fixed', top: 0, left: 0, width: '100%',
        zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '26px', height: '26px', background: 'linear-gradient(135deg,#6366f1,#a855f7)',
            borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: '11px', color: '#fff',
          }}>Qx</div>
          <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: '14px', color: '#eeeef5' }}>
            Qix<span style={{ color: '#818cf8' }}>Deploy</span>
          </span>
        </div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#8e8ea8', padding: '4px' }}
        >
          {isOpen ? (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 3l12 12M15 3L3 15"/>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M2 5h14M2 9h14M2 13h14"/>
            </svg>
          )}
        </button>
      </header>

      {/* Mobile drawer */}
      {isOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(3px)', zIndex: 200,
          }}
          onClick={() => setIsOpen(false)}
        >
          <aside
            style={{
              width: '260px', height: '100vh', background: '#111116',
              borderRight: '1px solid #26262f', display: 'flex',
              flexDirection: 'column', paddingTop: '52px',
            }}
            onClick={e => e.stopPropagation()}
          >
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;